import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { ToastContainer } from "react-toastify";

import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import * as api from "../../../shared/lib/api";
import { useCore } from "../../../shared/hooks/use-core";
import { Paginator } from "../../../shared/components/paginator";
import SearchWithFilter, {
	SearchFilterOption,
} from "../../../shared/components/search-with-filter";
import { onlyDigits } from "../../../shared/utils/only-digits";
import { onlyAlphanumeric } from "../../../shared/utils/only-alphanumeric";
import { MaskCNPJ, MaskCPF } from "../../../shared/utils/mask";
import UsersWebList, { UserWebListItem } from "./UsersWebList";

type ListUsersWebResponse = {
	content: {
		count: number;
		next: number | null;
		previous: number | null;
		results: UserWebListItem[];
	};
};

type PersonTypeFilter = "all" | "pf" | "pj";

const SEARCH_OPTIONS: SearchFilterOption[] = [
	{ value: "", label: "Todos", placeholder: "Nome, e-mail ou documento" },
	{ value: "nome", label: "Nome", placeholder: "Nome do usuário" },
	{ value: "email", label: "E-mail", placeholder: "E-mail do usuário" },
	{
		value: "cpf",
		label: "CPF",
		placeholder: "000.000.000-00",
		normalize: onlyDigits,
		mask: MaskCPF,
	},
	{
		value: "cnpj",
		label: "CNPJ",
		placeholder: "00.000.000/0000-00",
		normalize: onlyAlphanumeric,
		mask: MaskCNPJ,
	},
];

function matchesSearch(
	item: UserWebListItem,
	type: string,
	term: string,
): boolean {
	if (!term) return true;

	const lower = term.toLowerCase();
	const name = `${item.first_name} ${item.last_name}`.toLowerCase();
	const email = item.email.toLowerCase();
	const cpf = onlyDigits(item.cpf ?? "");
	const cnpj = onlyAlphanumeric(item.company?.cnpj ?? "");

	switch (type) {
		case "nome":
			return name.includes(lower);
		case "email":
			return email.includes(lower);
		case "cpf":
			return Boolean(cpf) && cpf.includes(term);
		case "cnpj":
			return Boolean(cnpj) && cnpj.includes(term.toUpperCase());
		default:
			return (
				name.includes(lower) ||
				email.includes(lower) ||
				(item.cpf?.toLowerCase().includes(lower) ?? false) ||
				(item.company?.cnpj?.toLowerCase().includes(lower) ?? false)
			);
	}
}

export default function UsersWeb(): JSX.Element {
	const { setTitleBar, setPathTitleBar } = useCore();

	const [users, setUsers] = useState<UserWebListItem[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [count, setCount] = useState<number>(0);
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [searchType, setSearchType] = useState<string>("");
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [personType, setPersonType] = useState<PersonTypeFilter>("all");

	const buildListUrl = (page: number) =>
		`user/list_company_user/?profile=4&page=${page}`;

	const getUsers = async (url: string) => {
		const response = await api.get<ListUsersWebResponse>(url);
		setCount(response.content.count);
		setUsers(response.content.results);
		setLoading(false);
	};

	const handleChangePage = async (
		_event: React.ChangeEvent<unknown>,
		value: number,
	) => {
		setLoading(true);
		setPageNumber(value);
		await getUsers(buildListUrl(value));
	};

	const usersSearch = useMemo(
		() =>
			users.filter(
				(item) =>
					matchesSearch(item, searchType, searchTerm) &&
					(personType === "all" ||
						(personType === "pf" && Boolean(item.cpf)) ||
						(personType === "pj" && Boolean(item.company))),
			),
		[users, searchType, searchTerm, personType],
	);

	useEffect(() => {
		getUsers(buildListUrl(1)).catch(console.error);
	}, []);

	useLayoutEffect(() => {
		setTitleBar("Usuários Web");
		setPathTitleBar("/new-user-web");
	}, [setPathTitleBar, setTitleBar]);

	return (
		<>
			<ToastContainer />
			<List dense disablePadding sx={{ width: "100%" }}>
				<ListItem
					disablePadding
					sx={{
						display: "flex",
						alignItems: "stretch",
						flexDirection: { xs: "column", sm: "row" },
						gap: { xs: 0, sm: 2 },
					}}
				>
					<Box sx={{ m: 1, flex: 1 }}>
						<SearchWithFilter
							options={SEARCH_OPTIONS}
							type={searchType}
							term={searchTerm}
							onTypeChange={setSearchType}
							onTermChange={setSearchTerm}
						/>
					</Box>

					<FormControl
						sx={{ m: 1, minWidth: { xs: "auto", sm: 180 } }}
						variant="standard"
					>
						<InputLabel id="person-type-label">Tipo de documento</InputLabel>
						<Select
							labelId="person-type-label"
							id="person-type-filter"
							value={personType}
							onChange={(e: SelectChangeEvent) =>
								setPersonType(e.target.value as PersonTypeFilter)
							}
						>
							<MenuItem value="all">Todos</MenuItem>
							<MenuItem value="pf">CPF - Pessoa Física</MenuItem>
							<MenuItem value="pj">CNPJ - Pessoa Jurídica</MenuItem>
						</Select>
					</FormControl>
				</ListItem>
			</List>
			<UsersWebList users={usersSearch} loading={loading} />
			<Paginator
				count={count}
				pageNumber={pageNumber}
				onChange={handleChangePage}
			/>
		</>
	);
}
